import React, { useState } from 'react';
import { UseFormRegister, UseFormSetValue, UseFormWatch, FieldErrors } from 'react-hook-form';
import { Upload, FileText, AlertCircle, CheckCircle, Copy, Loader2, Download } from 'lucide-react';
import Button from '../UI/Button';
import toast from 'react-hot-toast';
import apiService from '../../services/api';

interface FirebaseConfigStepProps {
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  errors: FieldErrors<any>;
  onKeystoreGenerated?: (keystoreData: {
    keystoreSha1?: string;
    keystoreSha256?: string;
    keystoreBuffer?: string; // Base64 buffer
    keystorePassword?: string;
    keystoreAlias?: string;
  } | null) => void;
  googleServicesJsonPreview?: string | null;
  googleServicesPlistPreview?: string | null;
  onGoogleServicesJsonPreviewChange?: (preview: string | null) => void;
  onGoogleServicesPlistPreviewChange?: (preview: string | null) => void;
  isEditMode?: boolean;
  editData?: Record<string, any>;
}

const FirebaseConfigStep: React.FC<FirebaseConfigStepProps> = ({
  register,
  setValue,
  watch,
  errors,
  onKeystoreGenerated,
  googleServicesJsonPreview,
  googleServicesPlistPreview,
  onGoogleServicesJsonPreviewChange,
  onGoogleServicesPlistPreviewChange,
  editData,
  isEditMode,
}) => {
  const [isGeneratingSHA, setIsGeneratingSHA] = useState(false);

  const platform = watch('platform');
  const packageName = watch('packageName');
  const sha1 = watch('sha1');
  const sha256 = watch('sha256');
  const keystoreBuffer = watch('keystoreBuffer');

  // Generate real SHA1 and SHA256 from keystore
  const generateKeystoreSHA = async () => {
    if (!packageName) {
      toast.error('Package name is required to generate keystore');
      return;
    }

    setIsGeneratingSHA(true);
    try {
      // Use dedicated keystore generation API
      const result = await apiService.generateKeystore(packageName);

      if (result.keystoreSha1 && result.keystoreSha256) {
        setValue('sha1', result.keystoreSha1);
        setValue('sha256', result.keystoreSha256);
        // Also set the keystore fields in the form
        setValue('keystorePassword', result.keystorePassword);
        setValue('keystoreAlias', result.keystoreAlias);
        setValue('keystoreBuffer', result.keystoreBuffer);

        // Store complete keystore data for final submission (including buffer)
        const keystoreData = {
          keystoreSha1: result.keystoreSha1,
          keystoreSha256: result.keystoreSha256,
          keystoreBuffer: result.keystoreBuffer, // Base64 buffer from backend
          keystorePassword: result.keystorePassword,
          keystoreAlias: result.keystoreAlias
        };

        if (onKeystoreGenerated) {
          onKeystoreGenerated(keystoreData);
        }

        toast.success('SHA fingerprints generated from keystore successfully! (Temporary - will upload on save)');
      } else {
        toast.error('Failed to generate keystore fingerprints');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to generate keystore';
      toast.error(`Keystore generation failed: ${errorMessage}`);
    } finally {
      setIsGeneratingSHA(false);
    }
  };

  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${type} copied to clipboard`);
  };

  const downloadKeystore = async () => {
    // If we have the buffer in memory (newly generated), download from buffer
    if (keystoreBuffer) {
      try {
        // Convert base64 to binary
        const binaryString = atob(keystoreBuffer);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        
        // Create blob from binary data
        const blob = new Blob([bytes], { type: 'application/octet-stream' });
        
        // Create download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${packageName || 'keystore'}.keystore`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the URL object
        URL.revokeObjectURL(url);
        
        toast.success('Keystore file downloaded successfully');
      } catch (error) {
        toast.error('Failed to download keystore file');
      }
      return;
    }

    // If we're in edit mode and have SHA fingerprints but no buffer,
    // the keystore was generated previously and stored on the server.
    // Use the proxy endpoint to ensure .keystore extension.
    if (isEditMode && sha1 && sha256 && editData?.keystoreUrl) {
      try {
        const rawUrl = editData.keystoreUrl as string;
        let filePath: string;

        // Extract the file path from the stored URL
        if (rawUrl.startsWith('private://')) {
          filePath = rawUrl.replace('private://', '');
        } else if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
          // For public URLs, extract the path after the domain
          // e.g., "https://...spaces.com/com.jd.test/security/..." -> "com.jd.test/security/..."
          const urlObj = new URL(rawUrl);
          filePath = urlObj.pathname.substring(1); // Remove leading /
        } else {
          // Assume it's already a path
          filePath = rawUrl;
        }

        // Use the proxy endpoint which ensures .keystore extension
        await apiService.downloadKeystoreProxy(filePath);
        toast.success('Keystore file downloaded successfully');
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || '';
        if (errorMessage.includes('not found') || errorMessage.includes('not configured')) {
          toast.error('Keystore not found. Please regenerate the keystore to download it.');
        } else {
          toast.error('Failed to download existing keystore. Please try again or regenerate the keystore.');
        }
      }
      return;
    }

    // If no buffer and no usable URL available
    toast.error('Keystore file not available to download in this session. Please regenerate the keystore to download it.');
  };

  const handleGoogleServicesFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setValue('googleServicesJson', file);
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      onGoogleServicesJsonPreviewChange?.(previewUrl);
      toast.success('Google Services JSON uploaded');
    }
  };

  const handleGoogleServicesPlistFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setValue('googleServicesPlist', file);
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      onGoogleServicesPlistPreviewChange?.(previewUrl);
      toast.success('Google Services Plist uploaded');
    }
  };

  return (
    <div className="space-y-6">

      {/* SHA Fingerprints */}
      { ['android', 'both'].includes(platform) && <div className="space-y-4">
        <h4 className="text-lg font-medium text-gray-900">SHA Fingerprints</h4>
        <p className="text-sm text-gray-600">
          These fingerprints are used to authenticate your app with Firebase. 
          They are generated from your app's signing certificate.
        </p>

        <div className="space-y-4">
          {/* SHA Generation Button */}
          <div className="flex items-center justify-between">
            <div>
              <h5 className="text-sm font-medium text-gray-700">Keystore Fingerprints</h5>
              <p className="text-xs text-gray-500">Generate SHA1 and SHA256 from your app's keystore</p>
            </div>
            <div className="flex space-x-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={generateKeystoreSHA}
                disabled={isGeneratingSHA || !packageName || (sha1 && sha256)}
              >
                {isGeneratingSHA ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Generating...
                  </>
                ) : (sha1 && sha256) ? (
                  'Already Generated'
                ) : (
                  'Generate Keystore'
                )}
              </Button>
              {(sha1 && sha256) && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={downloadKeystore}
                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Keystore
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setValue('sha1', '');
                      setValue('sha256', '');
                      setValue('keystorePassword', '');
                      setValue('keystoreAlias', '');
                      setValue('keystoreBuffer', '');
                      if (onKeystoreGenerated) {
                        onKeystoreGenerated(null);
                      }
                    }}
                    disabled={isGeneratingSHA || !packageName || (sha1 && sha256)}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    Clear
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* SHA1 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SHA1 Fingerprint *
            </label>
            <div className="flex space-x-2">
              <input
                {...register('sha1', { required: 'SHA1 fingerprint is required' })}
                type="text"
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm font-mono"
                placeholder="AA:BB:CC:DD:EE:FF:00:11:22:33:44:55:66:77:88:99:AA:BB:CC:DD"
                value={sha1 || ''}
              />
              {sha1 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(sha1, 'SHA1')}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              )}
            </div>
            {errors.sha1 && (
              <p className="mt-1 text-sm text-red-600">{errors.sha1.message as string}</p>
            )}
          </div>

          {/* SHA256 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SHA256 Fingerprint *
            </label>
            <div className="flex space-x-2">
              <input
                {...register('sha256', { required: 'SHA256 fingerprint is required' })}
                type="text"
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm font-mono"
                placeholder="AA:BB:CC:DD:EE:FF:00:11:22:33:44:55:66:77:88:99:AA:BB:CC:DD:EE:FF:00:11:22:33:44:55:66:77:88:99"
                value={sha256 || ''}
              />
              {sha256 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(sha256, 'SHA256')}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              )}
            </div>
            {errors.sha256 && (
              <p className="mt-1 text-sm text-red-600">{errors.sha256.message as string}</p>
            )}
          </div>
        </div> 
      </div> }
      
      {/* Google Client API (Create Only) */}
      {!isEditMode && (
        <div className="space-y-4">
          <h4 className="text-lg font-medium text-gray-900">Google Client API</h4>
          <p className="text-sm text-gray-600">
            Provide the Google OAuth client IDs for your app. These are usually needed for Google Sign-In
            and other Google services and can be found in your Google Cloud Console.
            <span className="text-red-500"> * </span>
          </p>

          <div className="space-y-4">
            {/* Android Google Client API */}
            {(['android', 'both'].includes(platform)) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Google Client API (Android) *
                </label>
                <input
                  type="text"
                  {...register('googleClientApiAndroid')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="e.g. 1234567890-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx.apps.googleusercontent.com"
                />
                {errors.googleClientApiAndroid && (
                  <p className="mt-1 text-sm text-red-600">{errors.googleClientApiAndroid.message as string}</p>
                )}
              </div>
            )}

            {/* iOS Google Client API */}
            {(['ios', 'both'].includes(platform)) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Google Client API (iOS) *
                </label>
                <input
                  type="text"
                  {...register('googleClientApiIos')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="e.g. 1234567890-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx.apps.googleusercontent.com"
                />
                {errors.googleClientApiIos && (
                  <p className="mt-1 text-sm text-red-600">{errors.googleClientApiIos.message as string}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Firebase Configuration Files */}
      <div className="space-y-4">
        <h4 className="text-lg font-medium text-gray-900">Firebase Configuration Files</h4>
        <p className="text-sm text-gray-600">
          Upload the Firebase configuration files for your selected platform(s).
        </p>

        <div className="space-y-4">
          {/* Google Services JSON (Android) */}
          {(platform === 'android' || platform === 'both') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Google Services JSON (Android) *
              </label>
              <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                googleServicesJsonPreview 
                  ? 'border-green-300 bg-green-50' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}>
                <input
                  type="file"
                  accept=".json"
                  onChange={handleGoogleServicesFile}
                  className="hidden"
                  id="google-services-json"
                  disabled={!!googleServicesJsonPreview}
                />
                <label
                  htmlFor="google-services-json"
                  className={`flex flex-col items-center space-y-2 ${
                    googleServicesJsonPreview ? 'cursor-default' : 'cursor-pointer'
                  }`}
                >
                  {googleServicesJsonPreview ? (
                    <div className="flex flex-col items-center space-y-2">
                      <div className="flex items-center space-x-2 text-green-600">
                        <CheckCircle className="h-6 w-6" />
                        <span className="font-medium">{watch('googleServicesJson')?.name || 'Google Services JSON'}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setValue('googleServicesJson', undefined);
                          onGoogleServicesJsonPreviewChange?.(null);
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                      >
                        Replace file
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Upload google-services.json</p>
                        <p className="text-xs text-gray-500">Download from Firebase Console</p>
                      </div>
                    </>
                  )}
                </label>
              </div>
            </div>
          )}

          {/* Google Services Plist (iOS) */}
          {(platform === 'ios' || platform === 'both') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Google Services Plist (iOS) *
              </label>
              <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                googleServicesPlistPreview 
                  ? 'border-green-300 bg-green-50' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}>
                <input
                  type="file"
                  accept=".plist"
                  onChange={handleGoogleServicesPlistFile}
                  className="hidden"
                  id="google-services-plist"
                  disabled={!!googleServicesPlistPreview}
                />
                <label
                  htmlFor="google-services-plist"
                  className={`flex flex-col items-center space-y-2 ${
                    googleServicesPlistPreview ? 'cursor-default' : 'cursor-pointer'
                  }`}
                >
                  {googleServicesPlistPreview ? (
                    <div className="flex flex-col items-center space-y-2">
                      <div className="flex items-center space-x-2 text-green-600">
                        <CheckCircle className="h-6 w-6" />
                        <span className="font-medium">{watch('googleServicesPlist')?.name || 'GoogleService-Info.plist'}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setValue('googleServicesPlist', undefined);
                          onGoogleServicesPlistPreviewChange?.(null);
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                      >
                        Replace file
                      </button>
                    </div>
                  ) : (
                    <>
                      <FileText className="h-8 w-8 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Upload GoogleService-Info.plist</p>
                        <p className="text-xs text-gray-500">Download from Firebase Console</p>
                      </div>
                    </>
                  )}
                </label>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-blue-900">Firebase Setup Instructions</h4>
            <div className="text-sm text-blue-700 mt-1 space-y-1">
              <p>1. Go to your Firebase Console and create a new project</p>
              <p>2. Add your app with the package name and iOS bundle ID</p>
              <p>3. Download the configuration files for your platform(s)</p>
              <p>4. Upload the files here to complete the setup</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirebaseConfigStep;
